import React, { useState } from 'react';
import Datepicker from 'react-tailwindcss-datepicker';
import DatePresetTabs from './DatePresetTabs';

const START_FROM = new Date();
START_FROM.setMonth(START_FROM.getMonth() + 1);

const DateRangePicker = ({ startDate, endDate, onDateChange }) => {
  const formatInitialDate = (date) => {
    if (!date) return null;
    return new Date(date);
  };

  const [tempDateValue, setTempDateValue] = useState({
    startDate: formatInitialDate(startDate),
    endDate: formatInitialDate(endDate)
  });

  const [currentRange, setCurrentRange] = useState(null);

  const predefinedRanges = [
    { label: 'Today', days: 0 },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 3 days', days: 3 },
    { label: 'Last 7 days', days: 7 },
    { label: 'Last 30 days', days: 30 },
    { label: 'This month', days: 'month' },
    { label: 'Last month', days: 'last-month' },
  ];

  const handleRangeSelect = (range) => {
    setCurrentRange(range.label);
    const today = new Date();
    today.setHours(12, 0, 0, 0);

    let newStart, newEnd;

    switch (range.days) {
      case 0:
        newStart = new Date(today);
        newEnd = new Date(today);
        break;

      case 1:
        newStart = new Date(today);
        newStart.setDate(newStart.getDate() - 1);
        newEnd = new Date(newStart);
        break;

      case 'month':
        newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        newStart.setHours(12, 0, 0, 0);
        newEnd = new Date(today);
        break;

      case 'last-month':
        newStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newStart.setHours(12, 0, 0, 0);
        newEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        newEnd.setHours(12, 0, 0, 0);
        break;

      default:
        newStart = new Date(today);
        newStart.setDate(newStart.getDate() - range.days + 1);
        newEnd = new Date(today);
    }

    const newValue = {
      startDate: newStart,
      endDate: newEnd
    };

    setTempDateValue(newValue);

    if (onDateChange) {
      const formattedStart = formatDateToISO(newStart);
      const formattedEnd = formatDateToISO(newEnd);
      onDateChange(formattedStart, formattedEnd);
    }
  };

  const formatDateToISO = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleValueChange = (newValue) => {
    setCurrentRange(null);
    const processedValue = {
      startDate: newValue?.startDate ? new Date(newValue.startDate) : null,
      endDate: newValue?.endDate ? new Date(newValue.endDate) : null
    };
    setTempDateValue(processedValue);
  };

  const handleApply = () => {
    if (tempDateValue?.startDate && tempDateValue?.endDate && onDateChange) {
      const formattedStart = formatDateToISO(tempDateValue.startDate);
      const formattedEnd = formatDateToISO(tempDateValue.endDate);
      onDateChange(formattedStart, formattedEnd);
    }
  };

  return (
    <div className="bg-card border border-line rounded-xl p-4 w-full">
      <div className="mb-4">
        <DatePresetTabs
          presets={predefinedRanges}
          activeLabel={currentRange}
          onSelect={handleRangeSelect}
        />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div className="w-full sm:w-80 react-tailwindcss-datepicker">
          <Datepicker
            readOnly
            useRange
            startFrom={START_FROM}
            startWeekOn="mon"
            value={tempDateValue}
            onChange={handleValueChange}
            primaryColor="blue"
            separator="to"
            displayFormat="DD.MM.YYYY"
            placeholder="Select date range"
            inputClassName="w-full h-[38px] border border-line rounded-lg px-3 py-2 text-sm text-ink placeholder:text-ink-placeholder focus:outline-none focus:border-brand-500 transition-colors cursor-pointer select-none"
            containerClassName="relative"
            toggleClassName="hidden"
            popoverDirection="down"
            inputProps={{
              readOnly: true,
              inputMode: "none",
              style: { caretColor: "transparent" }
            }}
          />
        </div>

        <button
          className={`h-[38px] px-4 bg-brandFill hover:bg-brandFillHover text-white rounded-lg text-sm font-medium transition-colors focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus ${
            (!tempDateValue?.startDate || !tempDateValue?.endDate)
              ? 'bg-fill hover:bg-fill text-ink-disabled cursor-not-allowed'
              : ''
          }`}
          onClick={handleApply}
          disabled={!tempDateValue?.startDate || !tempDateValue?.endDate}
        >
          Apply
        </button>
      </div>
    </div>
  );
};

export default DateRangePicker;